import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useEffect, useRef, useState } from 'react';
import ComboboxBase from './ComboboxBase';

function normalizeQuery(str) {
    return (str || '').trim().replace(/\s+/g, ' ');
}

const SearchSelect = forwardRef(
    (
        {
            label,
            name,
            autocomplete,
            error,
            disableTab,
            hint,
            value,
            onChange,

            loadOptions,
            minChars = 2,
            debounceMs = 300,
            cache = true,

            getOptionKey = opt => opt?.id ?? opt?.value ?? JSON.stringify(opt),
            getOptionLabel = opt => opt?.label ?? '',
            getOptionSubLabel,
            onSelect,

            helpText = 'Søk og velg fra listen',
            loadingText = 'Søker...',
            noResultsText = 'Ingen treff',
            apiErrorText = 'Kunne ikke hente treff.',
            ...props
        },
        ref
    ) => {
        const [isOpen, setIsOpen] = useState(false);
        const [isLoading, setIsLoading] = useState(false);
        const [apiError, setApiError] = useState(null);
        const [results, setResults] = useState([]);

        const cacheRef = useRef(new Map());
        const abortRef = useRef(null);
        const debounceRef = useRef(null);

        const query = normalizeQuery(value);

        const runSearch = async q => {
            if (!loadOptions) return;

            if (cache && cacheRef.current.has(q)) {
                setResults(cacheRef.current.get(q));
                return;
            }

            if (abortRef.current) abortRef.current.abort();
            const ctrl = new AbortController();
            abortRef.current = ctrl;

            setIsLoading(true);
            setApiError(null);

            try {
                const options = (await loadOptions(q, { signal: ctrl.signal })) || [];
                if (cache) cacheRef.current.set(q, options);
                setResults(options);
            } catch (e) {
                if (e?.name !== 'AbortError') {
                    setApiError(apiErrorText);
                    setResults([]);
                }
            } finally {
                setIsLoading(false);
            }
        };

        useEffect(() => {
            setApiError(null);
            if (!isOpen) return;

            if (query.length < minChars) {
                setResults([]);
                setIsLoading(false);
                return;
            }

            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => runSearch(query), debounceMs);

            return () => {
                if (debounceRef.current) clearTimeout(debounceRef.current);
            };
        }, [query, isOpen, minChars, debounceMs]);

        useEffect(() => {
            return () => {
                if (abortRef.current) abortRef.current.abort();
                if (debounceRef.current) clearTimeout(debounceRef.current);
            };
        }, []);

        const handleInputChange = e => {
            onChange?.(e);
            setIsOpen(true);
        };

        const handleSelect = opt => {
            const nextValue = getOptionLabel(opt);
            onChange?.({ target: { name, value: nextValue } });
            onSelect?.(opt);
            setIsOpen(false);
        };

        const renderMeta = () => {
            if (apiError) return <span className="combobox-meta__error">{apiError}</span>;
            if (query.length < minChars)
                return (
                    <span className="combobox-meta__hint">
                        {helpText} (min. {minChars} tegn)
                    </span>
                );
            if (isLoading) return <span className="combobox-meta__hint">{loadingText}</span>;
            if (results.length === 0)
                return <span className="combobox-meta__hint">{noResultsText}</span>;
            return <span className="combobox-meta__hint">Velg fra listen</span>;
        };

        return (
            <ComboboxBase
                ref={ref}
                label={label}
                name={name}
                autocomplete={autocomplete}
                error={error}
                disableTab={disableTab}
                hint={hint}
                value={value}
                onInputChange={handleInputChange}
                onSelectOption={handleSelect}
                items={results}
                isLoading={isLoading}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                getOptionKey={getOptionKey}
                getOptionLabel={getOptionLabel}
                getOptionSubLabel={getOptionSubLabel}
                renderMeta={renderMeta}
                icon={<FontAwesomeIcon icon={faChevronDown} aria-hidden="true" />}
                {...props}
            />
        );
    }
);

SearchSelect.displayName = 'SearchSelect';
export default SearchSelect;
